import { useI18nContext, useUserContext } from '@lemonade-hq/nuiza';
import type { PropsWithChildren } from 'react';
import React, { createContext, useContext, useMemo } from 'react';
import { useNavContext } from './NavProvider';

export interface MenuOption {
  readonly title: string;
  readonly url?: URL;
}

export interface ModalItem {
  readonly title: string;
  readonly subtitle: string;
  readonly acceptAction: string;
  readonly cancelAction?: string;
}

export type MenuOptionsMap = Partial<Record<MenuOptionType, MenuOption>>;
export type ModalMap = Record<ModalType, ModalItem>;

// Notice that the order of the enum values is important for the menu to be rendered in the correct order
export enum MenuOptionType {
  Edit = 'Edit',
  StartOver = 'StartOver',
  Logout = 'Logout',
  AskUsAnything = 'AskUsAnything',
}

export enum ModalType {
  Edit = 'Edit',
  StartOver = 'StartOver',
  Exit = 'Exit',
  AskUsAnything = 'AskUsAnything',
}

export interface UniclientLayoutContextValue {
  readonly menu: MenuOptionsMap;
  readonly modal: ModalMap;
}

export const UniclientLayoutContext = createContext<UniclientLayoutContextValue>({
  menu: {},
  modal: {},
} as UniclientLayoutContextValue);

export const UniclientLayoutProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const {
    auth: { logoutUrl },
    nav: { homeUrl },
  } = useNavContext();
  const { s } = useI18nContext();
  const { user } = useUserContext();

  const value = useMemo(() => {
    const userOption = {
      [MenuOptionType.Logout]: {
        title: s('chat.header.menu.logout.title'),
        url: new URL(`${logoutUrl}${homeUrl !== '' ? `?redirect_url=${homeUrl}` : ''}`),
      },
    };
    return {
      menu: {
        [MenuOptionType.Edit]: {
          title: s('chat.header.menu.edit.title'),
        },
        [MenuOptionType.StartOver]: {
          title: s('chat.header.menu.start_over.title'),
        },
        [MenuOptionType.AskUsAnything]: {
          title: s('chat.header.menu.ask_us_anything.title'),
        },
        ...(user != null ? userOption : {}),
      },
      modal: {
        [ModalType.Edit]: {
          title: s('chat.header.modal.edit.title'),
          subtitle: s('chat.header.modal.edit.subtitle'),
          acceptAction: s('chat.header.modal.edit.action'),
          cancelAction: s('chat.header.modal.edit.accept_action'),
        },
        [ModalType.StartOver]: {
          title: s('chat.start_over_dialog.title'),
          subtitle: s('chat.start_over_dialog.content'),
          acceptAction: s('chat.history.start_over_dialog.action'),
          cancelAction: s('chat.history.start_over_dialog.secondary_action'),
        },
        [ModalType.Exit]: {
          title: s('chat.header.modal.exit.title'),
          subtitle: s('chat.header.modal.exit.subtitle'),
          acceptAction: s('chat.header.modal.exit.accept_action'),
        },
        [ModalType.AskUsAnything]: {
          title: s('chat.header.modal.ask_us_anything.title'),
          subtitle: s('chat.header.modal.ask_us_anything.subtitle'),
          acceptAction: s('chat.header.modal.ask_us_anything.action'),
        },
      },
    };
  }, [logoutUrl, homeUrl, s, user]);

  return <UniclientLayoutContext.Provider value={value}>{children}</UniclientLayoutContext.Provider>;
};

export function useUniclientLayoutContext(): UniclientLayoutContextValue {
  return useContext(UniclientLayoutContext);
}
